import { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  PlusCircle,
  ClipboardList,
  Camera,
  Settings,
  ChevronRight,
  Building2
} from 'lucide-react'
import { Button, Card, StatCard } from '../components/common'
import useSurveyStore from '../stores/surveyStore'
import { formatDate } from '../utils/helpers'
import { SURVEY_STATUS_LABELS, SURVEY_STATUS_COLORS } from '../utils/constants'
import useMobileLayout from '../hooks/useMobileLayout'
import MobileDashboard from '../components/mobile/MobileDashboard'

export default function HomePage() {
  const isMobile = useMobileLayout()
  const navigate = useNavigate()
  const surveys = useSurveyStore(state => state.surveys)
  const loadSurveys = useSurveyStore(state => state.loadSurveys)

  useEffect(() => {
    loadSurveys()
  }, [loadSurveys])

  // Render mobile dashboard for small screens
  if (isMobile) {
    return <MobileDashboard />
  }

  // Calculate stats
  const totalSurveys = surveys.length
  const inProgressSurveys = surveys.filter(s => s.status === 'in-progress').length
  const completedSurveys = surveys.filter(s => s.status === 'completed' || s.status === 'analysed').length

  // Get recent surveys (last 5)
  const recentSurveys = surveys.slice(0, 5)

  return (
    <div className="space-y-8">
      {/* Welcome section */}
      <div className="bg-gradient-to-r from-primary-700 to-primary-800 rounded-2xl p-6 md:p-8 text-white">
        <h1 className="text-2xl md:text-3xl font-bold mb-2">
          Welcome to Clystcore Surveyors
        </h1>
        <p className="text-primary-100 mb-6 max-w-2xl">
          Capture and analyse property survey photos with AI-powered insights.
          Start a new survey or continue where you left off.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="accent"
            icon="PlusCircle"
            onClick={() => navigate('/survey/new')}
            className="shadow-lg"
          >
            New Survey
          </Button>
          <Button
            variant="outline"
            icon="ClipboardList"
            onClick={() => navigate('/surveys')}
            className="border-white/30 text-white hover:bg-white/10"
          >
            View All Surveys
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          title="Total Surveys"
          value={totalSurveys}
          icon={ClipboardList}
        />
        <StatCard
          title="In Progress"
          value={inProgressSurveys}
          icon={Camera}
        />
        <StatCard
          title="Completed"
          value={completedSurveys}
          icon={Building2}
        />
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/survey/new">
            <Card hover className="text-center py-6">
              <PlusCircle className="w-10 h-10 text-primary-600 mx-auto mb-3" />
              <p className="font-medium text-slate-900">New Survey</p>
              <p className="text-sm text-slate-500 mt-1">Start a new property survey</p>
            </Card>
          </Link>
          <Link to="/surveys">
            <Card hover className="text-center py-6">
              <ClipboardList className="w-10 h-10 text-secondary-600 mx-auto mb-3" />
              <p className="font-medium text-slate-900">All Surveys</p>
              <p className="text-sm text-slate-500 mt-1">View and manage surveys</p>
            </Card>
          </Link>
          <Link to="/admin">
            <Card hover className="text-center py-6">
              <Settings className="w-10 h-10 text-accent-600 mx-auto mb-3" />
              <p className="font-medium text-slate-900">Categories</p>
              <p className="text-sm text-slate-500 mt-1">Manage survey categories</p>
            </Card>
          </Link>
          {inProgressSurveys > 0 && (
            <Link to={`/survey/${surveys.find(s => s.status === 'in-progress')?.id}`}>
              <Card hover className="text-center py-6 bg-amber-50 border-amber-200">
                <Camera className="w-10 h-10 text-amber-600 mx-auto mb-3" />
                <p className="font-medium text-slate-900">Continue Survey</p>
                <p className="text-sm text-amber-700 mt-1">Resume in-progress survey</p>
              </Card>
            </Link>
          )}
        </div>
      </div>

      {/* Recent Surveys */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900">Recent Surveys</h2>
          {surveys.length > 5 && (
            <Link
              to="/surveys"
              className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1"
            >
              View all
              <ChevronRight className="w-4 h-4" />
            </Link>
          )}
        </div>

        {recentSurveys.length > 0 ? (
          <div className="space-y-3">
            {recentSurveys.map((survey) => (
              <Card
                key={survey.id}
                hover
                onClick={() => navigate(`/survey/${survey.id}`)}
                className="flex items-center gap-4"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-6 h-6 text-primary-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 truncate">
                    {survey.propertyName}
                  </h3>
                  <p className="text-sm text-slate-500 truncate">
                    {survey.address}, {survey.postcode}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${SURVEY_STATUS_COLORS[survey.status]}`}>
                    {SURVEY_STATUS_LABELS[survey.status]}
                  </span>
                  <span className="text-sm text-slate-400 hidden sm:block">
                    {formatDate(survey.createdAt)}
                  </span>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="text-center py-12">
            <ClipboardList className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No surveys yet</h3>
            <p className="text-slate-500 mb-4">Create your first property survey to get started</p>
            <Button variant="primary" icon="PlusCircle" onClick={() => navigate('/survey/new')}>
              Create Survey
            </Button>
          </Card>
        )}
      </div>

    </div>
  )
}
